import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * **Model → view**: `cards` holds `{ t, c }` (text + column 0|1). `render()` clears lists and recreates `<li class="card">`.
 * DnD stores array **index** in `dataTransfer`; on `drop`, update `cards[dragI].c` and re-render. Counts read from DOM in `sync()`.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'kanban-lite',
  category: 'drag-drop',
  title: 'Kanban (two columns)',
  description: 'Drag cards between Todo and Doing; counts update.',
  tags: [T.kanban, T.dnd],
  html: `<h2>Mini board</h2>

<div class="board">
  <!-- Empty uls: cards injected by JS -->
  <div class="col"><h3>Todo <span id="c0" class="n">2</span></h3><ul id="t0" data-col="0"></ul></div>
  <div class="col"><h3>Doing <span id="c1" class="n">1</span></h3><ul id="t1" data-col="1"></ul></div>
</div>
<p class="small">Drag cards between lists.</p>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 1rem;
}

.board {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.col {
  flex: 1;
  min-width: 10rem;
  background: var(--nogrok-preview-surface-container);
  border: 1px solid var(--nogrok-preview-outline);
  border-radius: 10px;
  padding: 0.75rem;
}

.col h3 {
  margin: 0 0 0.65rem;
  font-size: 0.85rem;
  color: var(--nogrok-preview-on-surface-muted);
}

.n {
  font-size: 0.7rem;
  background: var(--nogrok-preview-surface-container-high);
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
}

.col ul {
  list-style: none;
  padding: 0;
  margin: 0;
  min-height: 4rem;
}

.card {
  margin-bottom: 8px;
  padding: 0.5rem 0.65rem;
  background: var(--nogrok-preview-surface-elevated);
  border: 1px solid color-mix(in srgb, var(--nogrok-preview-primary) 19%, transparent);
  border-radius: 6px;
  cursor: grab;
  font-size: 0.9rem;
}

.small {
  font-size: 0.75rem;
  margin-top: 0.75rem;
}`,
  js: `const u0 = document.getElementById('t0')
const u1 = document.getElementById('t1')
const cards = [
  { t: 'Design spec', c: 0 },
  { t: 'Implement DnD', c: 1 },
  { t: 'Write tests', c: 0 },
]

function render() {
  ;[u0, u1].forEach((u) => {
    if (u) u.innerHTML = ''
  })
  cards.forEach((x, i) => {
    const li = document.createElement('li')
    li.className = 'card'
    li.draggable = true
    li.textContent = x.t
    li.dataset.i = String(i)
    const u = x.c === 0 ? u0 : u1
    u?.appendChild(li)
  })
  sync()
}

function sync() {
  const n0 = u0?.querySelectorAll('.card').length ?? 0
  const n1 = u1?.querySelectorAll('.card').length ?? 0
  const e0 = document.getElementById('c0')
  const e1 = document.getElementById('c1')
  if (e0) e0.textContent = String(n0)
  if (e1) e1.textContent = String(n1)
}

render()

let dragI = -1

document.addEventListener('dragstart', (e) => {
  const t = e.target.closest?.('.card')
  if (!t) return
  dragI = Number(t.dataset.i)
  e.dataTransfer?.setData('text/plain', String(dragI))
})

document.addEventListener('dragover', (e) => {
  const ul = e.target.closest?.('ul[id^=t]')
  if (ul) e.preventDefault()
})

document.addEventListener('drop', (e) => {
  const ul = e.target.closest?.('ul[id^=t]')
  if (!ul || dragI < 0) return
  e.preventDefault()
  const col = ul.id === 't0' ? 0 : 1
  if (cards[dragI]) cards[dragI].c = col
  dragI = -1
  render()
})`,
}

export default preset
